"use client"
import { Inventaire, InventaireCreateInput, InventaireDuplicateInput, InventaireListResponse, InventaireRadiusResponse, InventaireResponse, InventaireUpdateInput, PrintDataFields, PrintDataResponse } from '@repo/types';
import { AxiosInstance } from 'axios';
import { safeGetItem, safeRemoveItem, safeSetItem } from '../../utils/localStorage';
import { axiosInstance } from '../axios-instance';
import { ENDPOINTS } from '../endpoint';
import { Coordinate } from '../types/inventory';

function buildStatisticsParams(
  startDate?: string,
  endDate?: string,
  code_site?: string,
  includeDrafts?: boolean
): URLSearchParams {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  if (code_site) params.append('code_site', code_site);
  if (includeDrafts === true) params.append('includeDrafts', 'true');
  return params;
}

export class InventoryService {
  private axios: AxiosInstance;

  constructor(axios: AxiosInstance = axiosInstance) {
    this.axios = axios;
  }

  async getOneInventaire(id: number): Promise<InventaireResponse> {
    const { data } = await this.axios.get(`${ENDPOINTS.INVENTORY.BY_ID(id.toString())}`);
    return data;
  }

  async getPrintData(id: number): Promise<PrintDataFields> {
    const { data } = await this.axios.get<PrintDataResponse>(`${ENDPOINTS.INVENTORY.BASE}/print-data/${id.toString()}`);
    return data.data;
  }

  async getMultipleInventaires(ids: number[]): Promise<InventaireResponse[]> {
    // Make parallel requests for multiple inventaires
    const promises = ids.map(id => this.getOneInventaire(id));
    return await Promise.all(promises);
  }

  async createInventaire(payload: InventaireCreateInput): Promise<InventaireResponse> {
    const { data } = await this.axios.post(`${ENDPOINTS.INVENTORY.BASE}`, payload);
    return data;
  }
  async updateInventaire(id: number, payload: InventaireUpdateInput): Promise<InventaireResponse> {
    const { data } = await this.axios.put(`${ENDPOINTS.INVENTORY.BY_ID(id.toString())}`, payload);
    return data;
  }

  async duplicateInventaire(id: number, payload: InventaireDuplicateInput = {}): Promise<InventaireResponse> {
    const { data } = await this.axios.post(`${ENDPOINTS.INVENTORY.BY_ID(id.toString())}/duplicate`, payload);
    return data;
  }

  async getInventaireFinished(
    page: number,
    limit: number,
    search?: string,
    type?: string,
    epaysage_type?: string,
    code_site?: string,
    sortBy?: string,
    sortOrder?: string
  ): Promise<InventaireListResponse> {
    const { data } = await this.axios.get(
      `${ENDPOINTS.INVENTORY.FINISHED}`,
      { params: { page, limit, search, type, epaysage_type, code_site, sortBy, sortOrder } }
    );
    return data;
  }

  async getInventaireNotFinished(
    page: number,
    limit: number,
    search?: string,
    type?: string,
    epaysage_type?: string,
    code_site?: string,
    sortBy?: string,
    sortOrder?: string
  ): Promise<InventaireListResponse> {
    const { data } = await this.axios.get(
      `${ENDPOINTS.INVENTORY.NOT_FINISHED}`,
      { params: { page, limit, search, type, epaysage_type, code_site, sortBy, sortOrder } }
    );
    return data;
  }

  async getAlerts(
    page: number,
    limit: number,
    search?: string,
    filter?: string,
    code_site?: string
  ): Promise<InventaireListResponse> {
    const { data } = await this.axios.get(
      `${ENDPOINTS.INVENTORY.ALERTS}`,
      { params: { page, limit, search, filter, code_site } }
    );
    return data;
  }

  async getVisites(
    page: number,
    limit: number,
    search?: string,
    code_site?: string
  ): Promise<InventaireListResponse> {
    const { data } = await this.axios.get(
      `${ENDPOINTS.INVENTORY.VISITES}`,
      { params: { page, limit, search, code_site } }
    );
    return data;
  }

  async getTravaux(
    page: number,
    limit: number,
    search?: string,
    code_site?: string
  ): Promise<InventaireListResponse> {
    const { data } = await this.axios.get(
      `${ENDPOINTS.TRAVAUX.INVENTAIRES}`,
      { params: { page, limit, search, code_site } }
    );
    return data;
  }

  async getInventairesByRadius(lat: number, lng: number, radius: number, page = 1, limit = 10, search?: string, code_site?: string, ville?: string, address?: string, arbre_remarquable?: boolean, brouillon?: boolean, alertes?: boolean, espece_id?: number): Promise<InventaireRadiusResponse> {
    // Convert radius from meters to kilometers for API
    const radiusInKm = radius / 1000;

    const { data } = await this.axios.get(
      `${ENDPOINTS.INVENTORY.RADIUS}`,
      {
        params: {
          lat,
          lng,
          radius: radiusInKm,
          page,
          limit,
          search,
          code_site,
          ville,
          address,
          arbre_remarquable,
          brouillon,
          alertes,
          espece_id
        }
      }
    );
    return data;
  }

  async deleteInventaires(ids: number[]): Promise<void> {
    await this.axios.post(`${ENDPOINTS.INVENTORY.BULK_DELETE}`, { ids });
  }

  async deleteInventaire(id: number): Promise<InventaireResponse> {
    const { data } = await this.axios.delete(`${ENDPOINTS.INVENTORY.BY_ID(id.toString())}`);
    return data;
  }

  async restoreInventaire(id: number): Promise<InventaireResponse> {
    const { data } = await this.axios.patch(`${ENDPOINTS.INVENTORY.BY_ID(id.toString())}/restore`);
    return data;
  }

  async getDeletedInventaires(page: number, limit: number, search?: string): Promise<InventaireListResponse> {
    const { data } = await this.axios.get(
      `${ENDPOINTS.INVENTORY.BASE}/deleted`,
      { params: { page, limit, search } }
    );
    return data;
  }

  async validInventaireIsFinished(ids: number[]): Promise<void> {
    await this.axios.post(`${ENDPOINTS.INVENTORY.VALIDATE}`, { ids });
  }

  async uploadImageArbre(files: File[], id: number, position?: number): Promise<void> {
    const formData = new FormData();

    if (position) {
      //@ts-ignore
      formData.append(`img${position}`, files[0]);
    } else {
      files.forEach((file, index) => {
        formData.append(`img${index + 1}`, file);
      });
    }

    await this.axios.post(`${ENDPOINTS.INVENTORY.IMAGES.UPLOAD(id.toString())}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async deleteImgArbre(position: number, id: number): Promise<void> {
    await this.axios.put(`${ENDPOINTS.INVENTORY.IMAGES.DELETE(id.toString(), position.toString())}`);
  }

  async rotateImg(position: number, id: number, rotation: number, type: string = 'arbre'): Promise<void> {
    await this.axios.post(`${ENDPOINTS.INVENTORY.IMAGES.ROTATE(id.toString())}`, {
      position,
      degre: rotation,
      type
    });
  }

  static isEB(type: string): boolean {
    return (type.toUpperCase() !== 'ARBRE') && (type.toUpperCase() !== 'ALIGNEMENT');
  }

  static concatCoordEpaysage(coord: Coordinate[]) {
    return {
      lat: coord.map(elt => elt.lat).toString(),
      lng: coord.map(elt => elt.lng).toString()
    };
  }

  // Storage methods for printing
  stockInventaireToPrint(inv: Inventaire): void {
    safeSetItem('inventaireToPrint', JSON.stringify(inv));
  }

  stockInventairesToPrint(invs: Inventaire[]): void {
    safeSetItem('inventairesToPrint', JSON.stringify(invs));
  }

  getInventaireToPrint(): Inventaire | null {
    const data = safeGetItem('inventaireToPrint');
    return data ? JSON.parse(data) : null;
  }

  getInventairesToPrint(): Inventaire[] | null {
    const data = safeGetItem('inventairesToPrint');
    return data ? JSON.parse(data) : null;
  }

  destroyInventaireToPrint(): void {
    safeRemoveItem('inventaireToPrint');
  }

  destroyInventairesToPrint(): void {
    safeRemoveItem('inventairesToPrint');
  }

  // Storage methods for printing IDs (more efficient than storing full objects)
  stockInventaireIdsToPrint(ids: number[]): void {
    safeSetItem('inventaireIdsToPrint', JSON.stringify(ids));
  }

  getInventaireIdsToPrint(): number[] | null {
    const data = safeGetItem('inventaireIdsToPrint');
    return data ? JSON.parse(data) : null;
  }

  destroyInventaireIdsToPrint(): void {
    safeRemoveItem('inventaireIdsToPrint');
  }

  // Export methods
  static extractFileJson(inventaires: Inventaire[]) {
    console.log(inventaires);
    // return inventaires.map(inv => {
    //   if (inv.type === "EP") {
    //     return {
    //       'ID unique': inv.id,
    //       "Surface en ha": "test"
    //     };
    //   }

    //   const address = inv.arbre?.address.split(',') || [];
    //   return {
    //     'ID unique': inv.id,
    //     'Date inventaire': '',//inv.createdAt ? moment(inv.createdAt).format('DD-MM-YYYY') : '',
    //     'Genre': inv.arbre?.espece.genre || '',
    //     'Espece': inv.arbre?.espece.name || '',
    //     'Cultivar': inv.arbre?.espece.cultivar || '',
    //     'Nom vernaculaire': inv.arbre?.espece.nomFr || '',
    //     'Nombre de sujets': inv.arbre?.numSujet || '',
    //     'Hauteur en m': inv.arbre?.hauteur || '',
    //     'Diametre en cm': inv.arbre?.diametre || '',
    //     'Adresse 1': address[0] || '',
    //     'Adresse 2': address[1] || '',
    //     'Ville': inv.arbre?.ville || '',
    //     'Pays': inv.arbre?.pays || '',
    //     // Add more fields as needed
    //   };
    // });
  }

  async getStatistics(
    startDate?: string,
    endDate?: string,
    code_site?: string,
    includeDrafts?: boolean
  ): Promise<any> {
    const params = buildStatisticsParams(startDate, endDate, code_site, includeDrafts);
    const { data } = await this.axios.get(
      `${ENDPOINTS.INVENTORY.STATISTICS}${params.toString() ? `?${params.toString()}` : ''}`
    );
    return data;
  }

  async getEtatSanitaireStatistics(
    startDate?: string,
    endDate?: string,
    code_site?: string,
    includeDrafts?: boolean
  ): Promise<{
    data: {
      total: number;
      byEtat: Array<{ etat: string; label: string; count: number; percentage: number }>;
    };
  }> {
    const params = buildStatisticsParams(startDate, endDate, code_site, includeDrafts);
    const { data } = await this.axios.get(
      `${ENDPOINTS.INVENTORY.ETAT_SANITAIRE_STATISTICS}${params.toString() ? `?${params.toString()}` : ''}`
    );
    return data;
  }

  async getEpaysageTypeStatistics(
    startDate?: string,
    endDate?: string,
    code_site?: string,
    includeDrafts?: boolean
  ): Promise<{
    data: {
      total: number;
      byType: Array<{ type: string; count: number; percentage: number }>;
    };
  }> {
    const params = buildStatisticsParams(startDate, endDate, code_site, includeDrafts);
    const { data } = await this.axios.get(
      `${ENDPOINTS.INVENTORY.STATISTICS_EPAYSAGE_TYPES}${params.toString() ? `?${params.toString()}` : ''}`
    );
    return data;
  }

  async getDetailedInventoryStatistics(
    startDate?: string,
    endDate?: string,
    code_site?: string,
    includeDrafts?: boolean
  ): Promise<{
    data: {
      total: number;
      byCategory: Array<{ category: string; count: number; percentage: number; color: string }>;
    };
  }> {
    const params = buildStatisticsParams(startDate, endDate, code_site, includeDrafts);
    const { data } = await this.axios.get(
      `${ENDPOINTS.INVENTORY.STATISTICS_DETAILED}${params.toString() ? `?${params.toString()}` : ''}`
    );
    return data;
  }

  async getGenresStatistics(
    startDate?: string,
    endDate?: string,
    code_site?: string,
    includeDrafts?: boolean
  ): Promise<{
    data: {
      genres: Array<{ genre: string; nomFr: string; nomLatin: string; count: number }>;
    };
  }> {
    const params = buildStatisticsParams(startDate, endDate, code_site, includeDrafts);
    const { data } = await this.axios.get(
      `${ENDPOINTS.INVENTORY.STATISTICS_GENRES}${params.toString() ? `?${params.toString()}` : ''}`
    );
    return data;
  }

  async getResineuxFeuillusStatistics(
    startDate?: string,
    endDate?: string,
    code_site?: string,
    includeDrafts?: boolean
  ): Promise<{
    data: {
      total: number;
      byCategory: Array<{ category: string; label: string; count: number; percentage: number; color: string }>;
    };
  }> {
    const params = buildStatisticsParams(startDate, endDate, code_site, includeDrafts);
    const { data } = await this.axios.get(
      `${ENDPOINTS.INVENTORY.STATISTICS_RESINEUX_FEUILLUS}${params.toString() ? `?${params.toString()}` : ''}`
    );
    return data;
  }

  async getStadeDevStatistics(
    startDate?: string,
    endDate?: string,
    code_site?: string,
    includeDrafts?: boolean
  ): Promise<{
    data: {
      total: number;
      byStade: Array<{ stade: string; label: string; count: number; percentage: number }>;
    };
  }> {
    const params = buildStatisticsParams(startDate, endDate, code_site, includeDrafts);
    const { data } = await this.axios.get(
      `${ENDPOINTS.INVENTORY.STATISTICS_STADE_DEV}${params.toString() ? `?${params.toString()}` : ''}`
    );
    return data;
  }
}